import {
  FieldValues,
  RegisterOptions,
  FieldArrayPath,
  useFieldArray,
  useFormContext,
  useWatch,
  useController,
} from 'react-hook-form';
import { FormArrayFieldProps } from '../types';
import { H4, Button } from '@snsw/react-component-library';
import { Box, FlexBox, YES_NO_OPTIONS } from '@onegov/react-component-library';
import { FormInputField } from '../FormInputField';
import { ReactNode, useEffect } from 'react';
import { FormRadioGroup } from '../selection';
import { useMount } from 'react-use';
import { nameToPascalCase } from '../utils/NameToPascalCaseHelper';

interface RadioModel {
  hasOtherNames: boolean | null;
}

interface PersonNameProps<TFieldValues extends FieldValues = FieldValues> {
  label?: string;
  helpMessage?: string;
  rules?: Omit<RegisterOptions<TFieldValues>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
}

export interface FormOtherFullNamesProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>
> extends Omit<FormArrayFieldProps<TFieldValues, TFieldArrayName>, 'rules' | 'errorMode'> {
  label?: string;
  helpMessage?: ReactNode;
  fieldLabel?: string;
  firstNameProps?: PersonNameProps<TFieldValues>;
  middleNameProps?: PersonNameProps<TFieldValues>;
  lastNameProps?: PersonNameProps<TFieldValues>;
}

export const FormOtherFullNames = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>
>(
  props: FormOtherFullNamesProps<TFieldValues, TFieldArrayName>
) => {
  const { name, label, helpMessage, fieldLabel, firstNameProps, middleNameProps, lastNameProps, shouldUnregister } =
    props;

  const { field: arrayField } = useController({
    name,
    shouldUnregister,
  });

  const { control, clearErrors } = useFormContext<TFieldValues>();
  const { fields, append, remove } = useFieldArray<TFieldValues, TFieldArrayName>({
    control,
    name,
    shouldUnregister,
  });
  const addNew = () => {
    append({ firstName: '', lastName: '', middleName: '' } as any, { shouldFocus: false });
  };

  // Inject a new field to the form, unregistered upon unmount
  const radioName = 'hasOtherNames';
  const { setValue: setRadioFieldValue } = useFormContext<RadioModel>();
  const radioFieldValue = useWatch<RadioModel>({
    name: radioName,
  });

  useMount(() => {
    setRadioFieldValue(radioName, arrayField.value ? arrayField.value.length > 0 : null);
  });

  useEffect(() => {
    if (!!radioFieldValue && fields.length === 0) addNew();
    else if (radioFieldValue === false) remove();
  }, [radioFieldValue]);

  const toPascalCase = (next: string) => {
    return nameToPascalCase(next);
  };

  return (
    <>
      <FlexBox direction="column" gap={1.5} alignItems="flex-start">
        <FormRadioGroup
          name={radioName}
          label={label || 'Do you have any previous legal name(s)?'}
          helpMessage={
            helpMessage ||
            'Names that have been registered and used on government-issued documents, such as a passport or driver licence. '
          }
          options={YES_NO_OPTIONS}
          rules={{
            validate: {
              required: (value: boolean | null) => (value == null ? 'You must select an option.' : undefined),
            },
          }}
          shouldUnregister={true}
        />

        {fields.map((field, index) => (
          <FlexBox direction="column" key={'wrapper' + field.id} alignSelf="stretch">
            <H4>{(fieldLabel ?? 'Name') + ' ' + (index + 1)}</H4>
            <FormInputField
              // react-hook-form is unable to figure out the type for dynamic name
              // @ts-ignore
              name={`${name}.${index}.firstName`}
              label={firstNameProps?.label || 'First name'}
              helpMessage={firstNameProps?.helpMessage}
              rules={{
                required: `${
                  fieldLabel?.replace(/name$/, firstNameProps?.label?.toLocaleLowerCase() || 'first name') ??
                  (firstNameProps?.label || 'First name')
                } is required.`,
                ...firstNameProps?.rules,
              }}
              shouldUnregister={shouldUnregister}
              onBlurTransform={toPascalCase}
            />

            <FormInputField
              // react-hook-form is unable to figure out the type for dynamic name
              // @ts-ignore
              name={`${name}.${index}.middleName`}
              label={middleNameProps?.label || 'Middle names'}
              helpMessage={middleNameProps?.helpMessage}
              optional
              rules={{ ...middleNameProps?.rules }}
              shouldUnregister={shouldUnregister}
              onBlurTransform={toPascalCase}
            />

            <FormInputField
              // react-hook-form is unable to figure out the type for dynamic name
              // @ts-ignore
              name={`${name}.${index}.lastName`}
              label={lastNameProps?.label || 'Last name'}
              helpMessage={lastNameProps?.helpMessage}
              rules={{
                required: `${
                  fieldLabel?.replace(/name$/, lastNameProps?.label?.toLocaleLowerCase() || 'last name') ??
                  (lastNameProps?.label || 'Last name')
                } is required.`,
                ...lastNameProps?.rules,
              }}
              shouldUnregister={shouldUnregister}
              onBlurTransform={toPascalCase}
            />

            {fields.length > 1 && (
              <Box mt={0.5}>
                <Button
                  variant="link"
                  onClick={() => {
                    remove(index);
                    clearErrors();
                  }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </FlexBox>
        ))}

        {radioFieldValue && (
          <Button variant="secondary" onClick={() => addNew()}>
            + Add another name
          </Button>
        )}
      </FlexBox>
    </>
  );
};
